import { useState, useCallback, useContext, createContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Badge {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  threshold: number;
  category: string;
  /** City a Founding Member badge was earned for. */
  city?: string | null;
}

export interface UserBadge {
  id: string;
  badge_id: string;
  earned_at: string;
  city?: string | null;
  badge: Badge;
}

export interface NewlyEarnedBadge {
  badge: Badge;
  earned_at: string;
}

// Context for badge celebration - will be provided by BadgeCelebrationProvider
interface BadgeCelebrationContextType {
  celebrateBadge: (badge: Badge) => void;
}

export const BadgeCelebrationContext = createContext<BadgeCelebrationContextType | null>(null);

// Fetch all available badges
export function useAllBadges() {
  return useQuery({
    queryKey: ['badges'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('badges')
        .select('*')
        .order('category', { ascending: true });

      if (error) throw error;
      return data as Badge[];
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });
}

// Fetch badges for a specific user
export function useUserBadges(userId: string | null) {
  return useQuery({
    queryKey: ['user-badges', userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('user_badges')
        .select(`
          id,
          badge_id,
          earned_at,
          city,
          badge:badges (*)
        `)
        .eq('user_id', userId)
        .order('earned_at', { ascending: false });

      if (error) throw error;
      // Carry the earned city onto the badge so every screen can fill "{city}".
      return ((data ?? []) as unknown as UserBadge[]).map((ub) => ({
        ...ub,
        badge: ub.badge ? { ...ub.badge, city: ub.city ?? null } : ub.badge,
      }));
    },
    enabled: !!userId,
  });
}

// Hook to check and potentially award badges after an action
export function useBadgeCheck() {
  const [isChecking, setIsChecking] = useState(false);
  const queryClient = useQueryClient();
  
  // Use context directly - it's okay if it's null
  const celebrationContext = useContext(BadgeCelebrationContext);

  const checkBadges = useCallback(async (
    userId: string,
    actionType: 'signup' | 'check_in' | 'review' | 'favorite' | 'submission' | 'vote' | 'photo' | 'focus_session',
    cafeId?: string
  ): Promise<NewlyEarnedBadge[]> => {
    setIsChecking(true);

    try {
      const { data, error } = await supabase.functions.invoke('check-badges', {
        body: { user_id: userId, action_type: actionType, cafe_id: cafeId, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }
      });

      if (error) {
        console.error('Badge check error:', error);
        return [];
      }

      const newlyEarned = data?.newly_earned || [];

      // Badge COUNTS change on every tracked action, so refresh progress
      // caches unconditionally (the "next badge" teaser + profile bars).
      queryClient.invalidateQueries({ queryKey: ['badge-progress', userId] });

      if (newlyEarned.length > 0) {
        // Invalidate user badges cache
        queryClient.invalidateQueries({ queryKey: ['user-badges', userId] });

        // Trigger celebration for each newly earned badge
        newlyEarned.forEach((earned: NewlyEarnedBadge) => {
          if (celebrationContext) {
            celebrationContext.celebrateBadge(earned.badge);
          }
        });
      }

      return newlyEarned;
    } catch (err) {
      console.error('Badge check failed:', err);
      return [];
    } finally {
      setIsChecking(false);
    }
  }, [queryClient, celebrationContext]);

  return { checkBadges, isChecking };
}
